// @ts-check

import { useCallback, useEffect, useRef, useState } from "react"
import {
  getCategoriesOfTasksOfClientId,
  getLazyClientTasksReportData,
  getModulesOfTasksOfClientId,
  getProjectsOfTasksOfClientId,
  getTeamMembersOfTasksOfClientId,
} from "../../../../../services/report.service"
import {
  formatCategoriesDropdownFilter,
  formatModulesDropdownFilter,
  formatProjectsDropdownFilter,
  formatTeamMembersDropdownFilter,
} from "../../../../../utils/report.utils"
import useToast from "../../../../../hooks/useToast.hook"
import { DateTime } from "luxon"
import { usePaginatedFetch } from "../../../../../hooks/usePaginatedFetch.hook"

/** @param {import("./useClientTasksReportData.hook.types").UseClientTasksReportDataParams} params */
export function useClientTasksReportData(params) {
  const { clientId, dateRange, filterValues = {} } = params
  const [startDate, endDate] = dateRange

  const [categories, setCategories] = useState(
    /** @type {import("../../../../../utils/report.utils").FormattedCategory[]} */ ([])
  )
  const [modules, setModules] = useState(
    /** @type {import("../../../../../utils/report.utils").FormattedModule[]} */ ([])
  )
  const [projects, setProjects] = useState(
    /** @type {import("../../../../../utils/report.utils").ProjectGroup[]} */ ([])
  )
  const [teamMembers, setTeamMembers] = useState(
    /** @type {import("../../../../../utils/report.utils").TeamMemberGroup[]} */ ([])
  )

  const [isLoadingMeta, setIsLoadingMeta] = useState(false)

  const metaRequestIdRef = useRef(0)

  const { setErrorMessage } = useToast()

  const {
    items: tasks,
    totals,
    hasNextPage,
    isLoading: isLoadingTasks,
    isFetchingNextPage,
    fetch: fetchTasks,
    reset: resetPagination,
    abort,
  } = usePaginatedFetch({
    fetchFn: async ({ cursor, signal }) => {
      const response = await getLazyClientTasksReportData({
        params: {
          clientId,
          startDate,
          endDate,
          cursorDate: cursor,
          filters: filterValues,
        },
        config: { signal },
      })

      const {
        tasks: newTasks = [],
        totals: rawTotals,
        cursor: responseCursor,
      } = response.result ?? {}

      const hasNewTasks = newTasks.length > 0

      return {
        batch: newTasks,
        hasMore: hasNewTasks && Boolean(responseCursor?.hasNextPage),
        nextCursor:
          hasNewTasks && responseCursor?.nextCursor ?
            DateTime.fromISO(responseCursor.nextCursor.split("T")[0])
          : null,
        totals: {
          sisuTime: rawTotals?.sisuTime ?? null,
          billedTime: rawTotals?.billedTime ?? null,
          timeDifference: rawTotals?.timeDifference ?? null,
        },
      }
    },
    onError: (error) => setErrorMessage({ message: error.message }),
    getItemKey: (item) => item.taskId,
  })

  const resetMeta = useCallback(() => {
    setCategories([])
    setModules([])
    setProjects([])
    setTeamMembers([])
  }, [])

  const fetchMeta = useCallback(async () => {
    if (!clientId || !startDate || !endDate) return

    const requestId = ++metaRequestIdRef.current
    setIsLoadingMeta(true)

    try {
      const [teamRes, catRes, modRes, projRes] = await Promise.all([
        getTeamMembersOfTasksOfClientId(clientId, startDate, endDate),
        getCategoriesOfTasksOfClientId(clientId, startDate, endDate),
        getModulesOfTasksOfClientId(clientId, startDate, endDate),
        getProjectsOfTasksOfClientId(clientId, startDate, endDate),
      ])

      if (metaRequestIdRef.current !== requestId) return

      setTeamMembers(formatTeamMembersDropdownFilter(teamRes.result))
      setCategories(formatCategoriesDropdownFilter(catRes.result, true))
      setModules(formatModulesDropdownFilter(modRes.result))
      setProjects(formatProjectsDropdownFilter(projRes.result))
    } catch (error) {
      if (error instanceof Error) {
        setErrorMessage({ message: error.message })
      }
    } finally {
      if (metaRequestIdRef.current === requestId) {
        setIsLoadingMeta(false)
      }
    }
  }, [clientId, startDate, endDate])

  const resetData = useCallback(() => {
    resetPagination()
    resetMeta()
  }, [resetPagination, resetMeta])

  const refreshData = useCallback(() => {
    resetData()
    fetchMeta()
    fetchTasks({ reset: true })
  }, [fetchMeta, fetchTasks, resetData])

  useEffect(() => {
    if (!clientId || !startDate || !endDate) return

    resetData()
    fetchMeta()
    fetchTasks({ reset: true })

    return () => abort()
  }, [clientId, startDate, endDate])

  useEffect(() => {
    if (!clientId || !startDate || !endDate) return

    resetPagination()
    fetchTasks({ reset: true })
  }, [filterValues])

  return {
    tasks,
    totals,
    categories,
    modules,
    projects,
    teamMembers,
    isFetchingNextPage,
    isLoading: isLoadingTasks || isLoadingMeta,
    refreshData,
    resetData,
    fetchNextPage: fetchTasks,
    hasNextPage,
  }
}
